// Immutable Audit Logging System

import { supabase } from '@/integrations/supabase/client';
import type { AuditAction, AuditLogEntry } from './types';
import { maskLogMessage } from './phiMasking';

interface AuditLogParams {
  action: AuditAction;
  resourceType: string;
  resourceId?: string;
  details?: Record<string, unknown>;
  phiAccessed?: boolean;
}

/**
 * Log an audit entry to the database
 * This is append-only - entries cannot be modified or deleted
 */
export async function logAudit(params: AuditLogParams): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.warn('Audit log attempted without authenticated user');
      return { success: false, error: 'No authenticated user' };
    }

    // Get user's client and role
    const { data: assignment } = await supabase
      .from('user_client_assignments')
      .select('client_id, role')
      .eq('user_id', user.id)
      .eq('is_primary', true)
      .maybeSingle();

    // Mask any PHI in details before logging
    const maskedDetails = params.details ? maskDetailsForLog(params.details) : {};

    const insertData = {
      client_id: assignment?.client_id,
      user_id: user.id,
      user_email: user.email,
      user_role: assignment?.role,
      action: params.action,
      resource_type: params.resourceType,
      resource_id: params.resourceId,
      details: JSON.parse(JSON.stringify(maskedDetails)),
      phi_accessed: params.phiAccessed || false,
      ip_address: null as string | null,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    const { error } = await supabase
      .from('audit_logs')
      .insert(insertData);

    if (error) {
      console.error('Failed to write audit log:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (err) {
    console.error('Audit logging error:', err);
    return { success: false, error: String(err) };
  }
}

/**
 * Mask PHI in log details
 */
function maskDetailsForLog(details: Record<string, unknown>): Record<string, unknown> {
  const masked: Record<string, unknown> = {};
  
  for (const [key, value] of Object.entries(details)) {
    if (typeof value === 'string') {
      // Check for PHI-sensitive field names
      const sensitiveFields = ['ssn', 'dob', 'phone', 'email', 'address', 'mrn', 'member_id'];
      if (sensitiveFields.some(f => key.toLowerCase().includes(f))) {
        masked[key] = '[MASKED]';
      } else {
        masked[key] = maskLogMessage(value);
      }
    } else if (typeof value === 'object' && value !== null) {
      masked[key] = maskDetailsForLog(value as Record<string, unknown>);
    } else {
      masked[key] = value;
    }
  }
  
  return masked;
}

/**
 * Log case creation
 */
export function logCaseCreated(caseId: string, caseNumber: string, patientId: string) {
  return logAudit({
    action: 'case_created',
    resourceType: 'authorization_request',
    resourceId: caseId,
    details: { case_number: caseNumber, patient_id: patientId },
    phiAccessed: true,
  });
}

/**
 * Log case approval
 */
export function logCaseApproved(caseId: string, caseNumber: string) {
  return logAudit({
    action: 'case_approved',
    resourceType: 'authorization_request',
    resourceId: caseId,
    details: { case_number: caseNumber },
  });
}

/**
 * Log submission initiated
 */
export function logSubmissionInitiated(caseId: string, lane: string, submissionLogId: string) {
  return logAudit({
    action: 'submission_initiated',
    resourceType: 'submission_log',
    resourceId: submissionLogId,
    details: { authorization_id: caseId, lane },
  });
}

/**
 * Log submission completed
 */
export function logSubmissionCompleted(submissionLogId: string, referenceId: string) {
  return logAudit({
    action: 'submission_completed',
    resourceType: 'submission_log',
    resourceId: submissionLogId,
    details: { reference_id: referenceId },
  });
}

/**
 * Log submission failed
 */
export function logSubmissionFailed(submissionLogId: string, error: string) {
  return logAudit({
    action: 'submission_failed',
    resourceType: 'submission_log',
    resourceId: submissionLogId,
    details: { error },
  });
}

/**
 * Log status change
 */
export function logStatusChanged(caseId: string, fromStatus: string, toStatus: string) {
  return logAudit({
    action: 'status_changed',
    resourceType: 'authorization_request',
    resourceId: caseId,
    details: { from_status: fromStatus, to_status: toStatus },
  });
}

/**
 * Log document uploaded
 */
export function logDocumentUploaded(documentId: string, caseId: string, documentType: string) {
  return logAudit({
    action: 'document_uploaded',
    resourceType: 'case_document',
    resourceId: documentId,
    details: { authorization_id: caseId, document_type: documentType },
    phiAccessed: true,
  });
}

/**
 * Log document removed
 */
export function logDocumentRemoved(documentId: string, caseId: string) {
  return logAudit({
    action: 'document_removed',
    resourceType: 'case_document',
    resourceId: documentId,
    details: { authorization_id: caseId },
  });
}

/**
 * Log override applied (completeness, etc.)
 */
export function logOverrideApplied(caseId: string, overrideType: string, reason: string) {
  return logAudit({
    action: 'override_applied',
    resourceType: 'authorization_request',
    resourceId: caseId,
    details: { override_type: overrideType, reason },
  });
}

/**
 * Log export generated
 */
export function logExportGenerated(exportType: string, recordCount: number, filters?: Record<string, unknown>) {
  return logAudit({
    action: 'export_generated',
    resourceType: 'export',
    details: { export_type: exportType, record_count: recordCount, filters },
    phiAccessed: true,
  });
}

/**
 * Log Epic pushback sent
 */
export function logEpicPushbackSent(pushbackLogId: string, destination: string, eventType: string) {
  return logAudit({
    action: 'epic_pushback_sent',
    resourceType: 'epic_pushback_log',
    resourceId: pushbackLogId,
    details: { destination, event_type: eventType },
  });
}

/**
 * Log Epic pushback failed
 */
export function logEpicPushbackFailed(pushbackLogId: string, error: string) {
  return logAudit({
    action: 'epic_pushback_failed',
    resourceType: 'epic_pushback_log',
    resourceId: pushbackLogId,
    details: { error },
  });
}

/**
 * Log sensitive data viewed
 */
export function logSensitiveDataViewed(resourceType: string, resourceId: string, dataType: string) {
  return logAudit({
    action: 'sensitive_data_viewed',
    resourceType,
    resourceId,
    details: { data_type: dataType },
    phiAccessed: true,
  });
}

/**
 * Log user login
 */
export function logUserLogin() {
  return logAudit({
    action: 'user_login',
    resourceType: 'auth',
  });
}

/**
 * Log user logout
 */
export function logUserLogout() {
  return logAudit({
    action: 'user_logout',
    resourceType: 'auth',
  });
}

/**
 * Log config changed
 */
export function logConfigChanged(configType: string, configId: string, changes: Record<string, unknown>) {
  return logAudit({
    action: 'config_changed',
    resourceType: configType,
    resourceId: configId,
    details: { changes },
  });
}

/**
 * Fetch audit logs with filters
 */
export async function fetchAuditLogs(params: {
  clientId?: string;
  userId?: string;
  action?: AuditAction;
  resourceType?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: AuditLogEntry[]; count: number }> {
  let query = supabase
    .from('audit_logs')
    .select('*', { count: 'exact' });

  if (params.clientId) {
    query = query.eq('client_id', params.clientId);
  }
  if (params.userId) {
    query = query.eq('user_id', params.userId);
  }
  if (params.action) {
    query = query.eq('action', params.action);
  }
  if (params.resourceType) {
    query = query.eq('resource_type', params.resourceType);
  }
  if (params.resourceId) {
    query = query.eq('resource_id', params.resourceId);
  }
  if (params.startDate) {
    query = query.gte('created_at', params.startDate);
  }
  if (params.endDate) {
    query = query.lte('created_at', params.endDate);
  }

  query = query
    .order('created_at', { ascending: false })
    .range(params.offset || 0, (params.offset || 0) + (params.limit || 50) - 1);

  const { data, count, error } = await query;

  if (error) {
    console.error('Failed to fetch audit logs:', error);
    return { data: [], count: 0 };
  }

  return { data: (data as AuditLogEntry[]) || [], count: count || 0 };
}
